import {
  isTransferDeed,
  latestTransfer,
  type OwnershipTransfer,
} from './assessment-reconciliation';
import { propertySectionLabel } from './field-labels';
import { isIntraFamilyTransfer, isLienReleaseTitle } from './liens';
import { permitThread } from './permit-history';
import { type DisplayRecord, isPermit } from './property-records';
import { documentTime } from './record-dates';
import type { PropertySummary, RecorderDocument, RecordKind } from './types';

const ACRONYMS = new Set([
  'ADU',
  'AC',
  'ADA',
  'AP',
  'CA',
  'CFC',
  'CC&R',
  'CC&RS',
  'DBI',
  'DPH',
  'DPW',
  'EV',
  'HVAC',
  'HOA',
  'ID',
  'II',
  'III',
  'IV',
  'LLC',
  'LP',
  'LTD',
  'MEP',
  'N/A',
  'NA',
  'NOV',
  'OOA',
  'PG&E',
  'PDR',
  'SF',
  'SFBC',
  'SFEBC',
  'SFFD',
  'SFMTA',
  'SFPUC',
  'SFUSD',
  'TCO',
  'UMB',
  'US',
  'USA',
]);

/**
 * City systems publish many free-text fields in ALL CAPS. Render them as
 * sentences while keeping identifiers, codes and known acronyms intact.
 */
export function cityText(value: string | null | undefined) {
  const text = (value ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  const letters = text.replace(/[^A-Za-z]/g, '');
  const upper = letters.replace(/[^A-Z]/g, '');
  if (letters.length < 4 || upper.length / letters.length < 0.75) return text;
  const lowered = text
    .split(' ')
    .map((token) => {
      const core = token.replace(/^[("'[]+|[)"'\].,;:!?]+$/g, '');
      if (!core) return token;
      if (ACRONYMS.has(core.toUpperCase()) || /^[a-z](?:\.[a-z])+$/i.test(core))
        return token.replace(core, core.toUpperCase());
      if (/\d/.test(core) || core.includes('/') || core.includes('#')) return token;
      if (/^[A-Z]&[A-Z]$/.test(core)) return token;
      return token.toLowerCase();
    })
    .join(' ');
  return lowered.replace(
    /(^|(?<![A-Z]\.[A-Z])[.!?]\s+)([a-z])/g,
    (_, lead: string, c: string) => lead + c.toUpperCase(),
  );
}

const NAME_LOWER = new Set(['and', 'of', 'the', 'de', 'la', 'del', 'van']);

/** Party names from the recorder index: title case with acronyms kept. */
export function cityName(value: string) {
  const text = cityText(value.replace(/^\((R|E)\)\s*/, ''));
  return text
    .split(' ')
    .map((token, index) => {
      if (token === token.toUpperCase()) return token;
      if (index > 0 && NAME_LOWER.has(token.toLowerCase())) return token;
      return token
        .split(/(-|')/)
        .map((part) => (/^[a-z]/.test(part) ? part[0]!.toUpperCase() + part.slice(1) : part))
        .join('');
    })
    .join(' ');
}

/** Records grouped by the public agency that publishes them. */
export type FeedSource =
  | 'dbi'
  | 'sf311'
  | 'sffd'
  | 'recorder'
  | 'public-works'
  | 'planning'
  | 'treasurer'
  | 'google';

export function feedSource(kind: RecordKind): FeedSource {
  switch (kind) {
    case 'street-view':
      return 'google';
    case '311':
      return 'sf311';
    case 'recorder':
      return 'recorder';
    case 'public-works':
      return 'public-works';
    case 'subdivision':
      return 'planning';
    case 'tax-bill':
      return 'treasurer';
    case 'fire-inspection':
    case 'fire-violation':
    case 'fire-compliance':
    case 'section604':
      return 'sffd';
    default:
      return 'dbi';
  }
}

const RECORDER_TITLES: Array<[RegExp, string]> = [
  [/^DEED OF TRUST/, 'Loan (deed of trust) recorded'],
  [/^ASS?GN\b.*DEED/, 'Loan transferred to a new lender'],
  [/^SUBORDT?N\b/, 'Loan subordination'],
  [/^SUBDNTN\b/, 'Subordination agreement'],
  [/RECONVEYANCE/, 'Loan paid off (reconveyance)'],
  [/SUBSTITUTION TRUSTEE/, 'Trustee substituted'],
  [/^ASGT RENTS/, 'Assignment of rents'],
  [/^RELEASE OF LIEN/, 'Lien released'],
  [/MECHANIC'?S LIEN/, "Mechanic's lien"],
  [/SPCL TAX LIEN/, 'Special tax lien notice'],
  [/^NTC LIEN/, 'Lien recorded'],
  [/^NTC COMPLETION/, 'Notice of completion'],
  [/^NTC SECURITY/, 'Security agreement notice'],
  [/^DECLARATION CC&R/, 'CC&Rs recorded'],
  [/RES ENERGY INSPCTN/, 'Residential energy inspection'],
  [/AFFIDAVIT CHANGE TTEE/, 'Change of trustee affidavit'],
  [/AFFIDAVIT OF DEATH/, 'Affidavit of death'],
  [/^AFFIDAVIT/, 'Affidavit'],
  [/^POWER ATTORNEY/, 'Power of attorney'],
  [/^DEED\b/, 'Deed (ownership transfer)'],
];

export function recorderTitle(title: string) {
  const upper = title.toUpperCase().trim();
  if (isLienReleaseTitle(upper) && !upper.startsWith('RELEASE OF LIEN'))
    return 'Lien cancellation recorded';
  const match = RECORDER_TITLES.find(([pattern]) => pattern.test(upper));
  return match ? match[1] : cityText(title);
}

export function recorderParties(names: string[]) {
  const grantors: string[] = [];
  const grantees: string[] = [];
  for (const name of names) {
    const role = /^\((R|E)\)/.exec(name)?.[1];
    (role === 'E' ? grantees : grantors).push(cityName(name));
  }
  return { grantors, grantees };
}

/** The latest arm's-length deed, falling back to any transfer deed. */
export function latestPurchase(summary: PropertySummary): OwnershipTransfer | null {
  let best: { time: number; document: RecorderDocument } | null = null;
  for (const document of summary.recorder?.documents ?? []) {
    if (!isTransferDeed(document) || isIntraFamilyTransfer(document)) continue;
    const time = documentTime(document.documentDate);
    if (time === null) continue;
    if (!best || time > best.time) best = { time, document };
  }
  if (best)
    return {
      documentNumber: best.document.documentNumber,
      documentDate: best.document.documentDate,
      names: best.document.names,
      sourceUrl: best.document.sourceUrl,
    };
  return latestTransfer(summary.recorder, null);
}

export type ThreadEntry = {
  key: string;
  date: string;
  label: string;
  status: string;
  comment: string;
  actor: string;
};

function headerKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Case history for a record: DBI enforcement updates, complaint case-history
 * tables, permit stages and inspections, or Planning project activities.
 */
export function recordThread(record: DisplayRecord): ThreadEntry[] {
  const entries: ThreadEntry[] = [];
  if (record.lien) {
    const entries = record.lien.documents.map((document, index) => ({
      key: document.id,
      date: document.date,
      label:
        index === 0
          ? 'Lien recorded'
          : record.lien!.match === 'ai-inferred'
            ? 'Likely release (AI matched)'
            : 'Lien released',
      status: '',
      comment:
        index > 0 && record.lien!.match === 'ai-inferred'
          ? `${document.id} · ${record.lien!.confidence ?? 'medium'} confidence; the Recorder index does not publish which release covers this lien.`
          : document.id,
      actor: '',
    }));
    if (record.lien.match === 'balanced-batch') {
      const latest = record.lien.batchReleases.at(-1);
      entries.push({
        key: `batch-${record.lien.batchReleases.map((item) => item.id).join('-')}`,
        date: latest?.date ?? '',
        label: 'Matching release batch',
        status: '',
        comment: `${record.lien.batchReleases.length} same-party release records found; the Recorder index does not publish an exact document pairing.`,
        actor: '',
      });
    }
    return entries;
  }
  if (record.enforcementUpdates?.length) {
    record.enforcementUpdates.forEach((item, index) =>
      entries.push({
        key: `enforcement-${index}`,
        date: item.date,
        label: item.type || propertySectionLabel(item.label),
        status: item.status,
        comment: item.comment,
        actor: item.inspector || item.updatedBy || item.actor,
      }),
    );
  } else if (record.kind === 'complaint' || record.kind === 'nov') {
    for (const page of record.pages ?? []) {
      for (const table of page.tables) {
        const headers = table.headers.map(headerKey);
        const date = headers.indexOf('date');
        const status = headers.indexOf('status');
        if (date < 0 || status < 0) continue;
        const type = headers.indexOf('type');
        const comment = headers.indexOf('comment');
        const inspector = headers.indexOf('inspector');
        table.rows.forEach((row, index) =>
          entries.push({
            key: `case-${index}`,
            date: row[date] ?? '',
            label: row[type] ?? '',
            status: row[status] ?? '',
            comment: row[comment] ?? '',
            actor: row[inspector] ?? '',
          }),
        );
      }
    }
  } else if (isPermit(record.kind)) {
    entries.push(...permitThread(record));
  }
  if (!entries.length && record.activities?.length) {
    record.activities.forEach((item) =>
      entries.push({
        key: item.key,
        date: item.date,
        label: item.activity,
        status: '',
        comment: item.comments,
        actor: '',
      }),
    );
  }
  const sorted = entries
    .filter((entry) => entry.label || entry.status || entry.comment)
    .map((entry, index) => ({ entry, index }))
    .sort(
      (a, b) =>
        (documentTime(a.entry.date) ?? 0) - (documentTime(b.entry.date) ?? 0) ||
        threadPhase(a.entry) - threadPhase(b.entry) ||
        a.index - b.index,
    )
    .map(({ entry }) => entry);
  const seen = new Set<string>();
  return sorted.filter((entry) => {
    const signature = [entry.date, entry.label, entry.status, entry.comment]
      .join('|')
      .toUpperCase();
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

/**
 * Within one day a permit's updates follow its lifecycle: intake and filing,
 * plan-review stations, approvals and issuance, then inspections and other
 * activity. Dates govern across days; this only orders same-day events that
 * the source lists in separate tables.
 */
function threadPhase(entry: ThreadEntry) {
  if (entry.key.startsWith('stage-'))
    return /intake|filed|filing|application|triage|received|submitted/i.test(entry.label) ? 0 : 2;
  if (entry.key.startsWith('addenda-')) return 1;
  if (/^(?:inspection|appointment|activity)-/.test(entry.key)) return 3;
  return 1;
}

function pageField(record: DisplayRecord, pattern: RegExp) {
  for (const page of record.pages ?? []) {
    for (const [key, value] of Object.entries(page.fields)) {
      if (pattern.test(key.replace(/[^a-z]/gi, '').toLowerCase()) && value) return value;
    }
  }
  return undefined;
}

const GENERIC = /open the official dbi record/i;

export type NovItem = { title: string; detail: string };

export type NovSummary = {
  /** The first cited violation, shortened to a single line. */
  headline: string;
  items: NovItem[];
  /** What DBI says the owner must do (e.g. "File for building permit …"). */
  action: string;
  /** Compliance period in days, when the notice states one. */
  days: number | null;
};

/** Housing-inspection notices repeat these rows on every NOV. */
const NOV_BOILERPLATE =
  /^(this notice includes violations|inspector'?s?'? comments?|miscellaneous violations)/i;

/** A table label DBI repeats on every notice rather than a cited violation. */
export const isNovBoilerplate = (text: string) => NOV_BOILERPLATE.test(text.trim());

const CODE = /\b(?:HC|PC|EC|BC|MC|SFBC|SFEBC)\b/i;

/** Drop "(712 HC)", "(701(c)(1) HC" style citations and trailing "SFBC Section …" references. */
function stripCodeCitations(value: string) {
  return value
    .replace(/\s*\((?:[^()]|\([^()]*\))*\)?/g, (match) => (CODE.test(match) ? '' : match))
    .replace(/\s*(?:S\.F\.(?:E\.)?B\.C\.?|SFE?BC\b).*$/i, '')
    .replace(/[\s.,;:]+$/, '')
    .trim();
}

/** "On September 15, 2017, we identified…" reads better without the preamble. */
function stripDatePreamble(value: string) {
  return value.replace(/^on\s+[a-z]+\s+\d{1,2},?\s+\d{4},?\s+/i, '');
}

function firstSentence(value: string) {
  const match = /^(.{12,}?[.!?])(?:\s|$)/.exec(value);
  return (match?.[1] ?? value).replace(/[.!?]$/, '');
}

function novItemsFromDescription(description: string): NovItem[] {
  const parts = description
    .replace(/¿/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .split(/(?:^|\s)\d{1,2}\.\)\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.map((part) => ({ title: plainViolation(part), detail: '' }));
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function plainViolation(value: string) {
  return stripCodeCitations(value) || value.trim();
}

const NOV_INSPECTOR_COMMENT = /^inspector'?s?'? comments?$/i;

function novItemsFromTables(record: DisplayRecord): NovItem[] {
  const items: NovItem[] = [];
  const comments: NovItem[] = [];
  for (const page of record.pages ?? []) {
    for (const table of page.tables) {
      const headers = table.headers.map(headerKey);
      const item = headers.indexOf('item');
      if (item < 0) continue;
      const description = headers.indexOf('description');
      for (const row of table.rows) {
        const title = (row[item] ?? '').trim();
        const detail = description < 0 ? '' : (row[description] ?? '').trim();
        // The inspector's note is the only specific text on some notices.
        if (NOV_INSPECTOR_COMMENT.test(title) && detail) {
          comments.push({ title: cityText(detail), detail: '' });
          continue;
        }
        if (!title || NOV_BOILERPLATE.test(title)) continue;
        items.push({
          title: cityText(plainViolation(title)),
          detail,
        });
      }
    }
  }
  return [...items, ...comments];
}

/**
 * DBI publishes two NOV layouts: enforcement notices with a free-text
 * violation description and a required correction, and housing-inspection
 * notices with a table of cited items. Both become one owner-facing summary.
 */
export function novSummary(record: DisplayRecord): NovSummary {
  const description = pageField(record, /^codeviolationdesc/) ?? '';
  const items = description ? novItemsFromDescription(description) : novItemsFromTables(record);
  const first = items[0]?.title ?? '';
  const headline = capitalize(
    cityText(stripCodeCitations(stripDatePreamble(firstSentence(first))) || first).slice(0, 160),
  );
  const days = Number.parseInt(
    pageField(record, /^(correctvioldays|dayscomp|complianceperiod)$/) ?? '',
    10,
  );
  return {
    headline,
    items,
    action: pageField(record, /^failurecommentdesc/) ?? '',
    days: Number.isFinite(days) && days > 0 ? days : null,
  };
}

/** Body copy that adds something beyond the title. */
export function recordBody(record: DisplayRecord) {
  if (record.ai) return record.ai.description;
  if (record.kind === 'nov') {
    return novSummary(record).items.length ? '' : (pageField(record, /^codeviolationdesc/) ?? '');
  }
  if (record.kind === 'complaint') return complaintSummary(record).body;
  return '';
}

const COMPLAINT_TITLE_MAX = 80;
/** Caller remarks about the report itself rather than the property. */
const COMPLAINT_META =
  /\b(?:anonymous|anonymity|confidential|do(?:es)? not want|caller|complainant|reporting party|call(?:ed)? (?:me|back)|contact (?:me|info))\b/i;
/** Intake channel prefixes such as "private_lot -- " or "Public right of way: ". */
const COMPLAINT_CHANNEL = /^\s*(?:[a-z]+(?:_[a-z]+)*\s*--|[a-z]+(?: [a-z]+){0,3}:)\s+(?=\S)/i;

export type ComplaintSummary = {
  /** The first sentence or clause of the complaint, short enough for a headline. */
  title: string;
  /** The complaint text when the title does not carry all of it. */
  body: string;
  /** The latest dated case-history status, e.g. "Case abated". */
  outcome: { date: string; status: string; label: string } | null;
};

/**
 * Deterministic title / description / resolution for a DBI complaint when no
 * AI explanation is stored, so the raw intake text is never shown twice.
 */
export function complaintSummary(record: DisplayRecord): ComplaintSummary {
  const raw = record.description?.trim() || pageField(record, /^description$/) || '';
  const text = cityText(complaintText(raw).replace(COMPLAINT_CHANNEL, '').trim());
  const clauses = text.split(/(?<=[.!?;])\s+|\s+--\s+/);
  const first = clauses.find((part) => !COMPLAINT_META.test(part)) ?? clauses[0] ?? '';
  const clause = first.replace(/[.;,\s]+$/, '').trim();
  const title =
    clause.length <= COMPLAINT_TITLE_MAX
      ? clause
      : `${clause.slice(0, COMPLAINT_TITLE_MAX).replace(/\s+\S*$/, '')}…`;
  const body = title && text.replace(/[.\s]+$/, '') !== title ? text : '';
  const latest = recordThread(record)
    .filter((entry) => entry.status)
    .at(-1);
  return {
    title,
    body,
    outcome: latest
      ? {
          date: latest.date,
          status: cityText(latest.status),
          label: cityText(latest.label),
        }
      : null,
  };
}

export function recordHeadline(record: DisplayRecord, kindLabel: string) {
  if (record.ai) return record.ai.title;
  const description = record.description?.trim() ?? '';
  if (record.kind === 'recorder') {
    const titles = description.split(',').map((title) => title.trim());
    return titles.map(recorderTitle).join(' + ');
  }
  if (record.kind === '311') {
    const [category] = description.split(' · ');
    if (/^(general request|other|other request)$/i.test(category?.trim() ?? '')) {
      const { type, details } = sf311Details(record);
      const specific = [...new Set([type, details].filter(Boolean))].join(' · ');
      if (specific) return capitalize(specific);
    }
    const { details } = sf311Details(record);
    const specific = [cityText(category), capitalize(details)]
      .filter(Boolean)
      .filter((part, index, parts) => parts.indexOf(part) === index)
      .join(' · ');
    return specific || kindLabel;
  }
  if (record.kind === 'nov') {
    const headline = novSummary(record).headline;
    if (headline) return headline;
    const complaint = pageField(record, /^complaintnumber$/);
    return complaint ? `${kindLabel} · complaint ${complaint}` : kindLabel;
  }
  if (!description || GENERIC.test(description)) return kindLabel;
  if (isPermit(record.kind)) {
    const permitDescription = description.replace(
      /^online\s+(?:building|electrical|plumbing)\s+permit\s*:?\s*/i,
      '',
    );
    const parts = permitDescription
      .split(/(?<=[a-z\d]{2}[.!?])\s+/i)
      .map((part) => part.trim())
      .filter(Boolean);
    const meaningful =
      parts.find(
        (part) =>
          !/^\d+\s+of\s+buildings?\s+of\s+\d+\s+dwelling\s+units?\s+or\s+less\.?$/i.test(part),
      ) ?? permitDescription;
    return cityText(meaningful).replace(/\bsffc\b/gi, 'SFFC') || kindLabel;
  }
  if (record.kind === 'complaint') return complaintSummary(record).title || kindLabel;
  return description;
}

/**
 * DBI complaint intake is a "field: value;" list ("date last observed: …;
 * additional information: …"). Keep the free-text part when there is one.
 */
export function complaintText(description: string) {
  if (!/^\s*(?:date|time)\s+last\s+observed\s*:/i.test(description)) return description;
  const info = /additional\s+information\s*:\s*([^;][\s\S]*)$/i.exec(description);
  const text = (info?.[1] ?? '').replace(/\s*;\s*$/, '').trim();
  if (text) return text;
  return (
    description
      .split(';')
      .map((part) => part.trim())
      .filter((part) => part && !/^[\w\s]+:\s*/.test(part))
      .join('; ') || description
  );
}

export function sf311Details(record: DisplayRecord) {
  const fields = record.pages?.[0]?.fields ?? {};
  const details = fields['Request details'] ?? '';
  const type = fields['Request type'] ?? '';
  const notes = fields['Status notes'] ?? '';
  const humanize = (value: string) =>
    cityText(value.replace(/_/g, ' ').replace(/\s+/g, ' ').trim());
  return {
    type: humanize(type),
    details: humanize(details),
    /** The reporter's own words, when the Open311 request supplied them. */
    description: (fields['Description'] ?? '').replace(/\s+/g, ' ').trim(),
    notes: notes.replace(/\s+/g, ' ').trim(),
    agency: fields['Responsible agency'] ?? '',
  };
}

export function isDirectImageUrl(url: string | undefined) {
  return Boolean(
    url &&
    /^https:\/\/spot-sf-res\.cloudinary\.com\/image\/upload\/v\d+\/san-francisco\/production\/[\w-]+\.(?:jpe?g|png|webp|gif)$/i.test(
      url,
    ),
  );
}

export function thumbnailUrl(url: string) {
  return url.replace('/image/upload/', '/image/upload/f_auto,q_auto,c_limit,w_800/');
}
